import type { SortDirection } from "@/types";

import { HttpClient, PaginatedResponse, robotoHeaders } from "../../http";

import {
  ImageRegistryRecord,
  ImageRepositoryRecord,
} from "./ImageRegistryRecord";

export class ImageRegistryService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getImagesForRepository(
    repository: string | null,
    options: Partial<{
      abortSignal: AbortSignal;
      orgId: string;
      pageToken: string;
    }>,
  ): Promise<PaginatedResponse<ImageRegistryRecord>> {
    if (!options.orgId) {
      throw Error(
        "getImagesForRepository requires an Org ID, none was provided",
      );
    }

    const searchParams = new URLSearchParams();
    if (repository) {
      searchParams.append("repository_name", repository);
    }
    if (options.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }

    const url = this.#httpClient.constructUrl(
      `v1/images/image/record/list`,
      searchParams,
    );

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
    });

    return await response.json<PaginatedResponse<ImageRegistryRecord>>();
  }

  /** Every image in `repository`, following `next_token` to the last page. */
  public async listImages(
    repository: string,
    options: { orgId: string; abortSignal?: AbortSignal },
  ): Promise<ImageRegistryRecord[]> {
    const items: ImageRegistryRecord[] = [];
    let pageToken: string | undefined = undefined;
    while (true) {
      const page: PaginatedResponse<ImageRegistryRecord> =
        await this.getImagesForRepository(repository, {
          ...options,
          pageToken,
        });
      items.push(...page.items);
      if (!page.next_token) {
        return items;
      }
      pageToken = page.next_token;
    }
  }

  /**
   * One page of the org's image repositories, ordered by name in
   * `sortDirection`. `next_token` is present only when another page exists.
   */
  public async getRepositoriesPage(options: {
    orgId: string;
    abortSignal?: AbortSignal;
    pageToken?: string;
    limit?: number;
    sortDirection?: SortDirection;
  }): Promise<PaginatedResponse<ImageRepositoryRecord>> {
    const searchParams = new URLSearchParams();
    if (options.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }
    if (options.limit !== undefined) {
      searchParams.append("limit", String(options.limit));
    }
    if (options.sortDirection !== undefined) {
      searchParams.append("sort_direction", options.sortDirection);
    }
    const url = this.#httpClient.constructUrl(
      "v1/images/repository/record/list",
      searchParams,
    );
    const response = await this.#httpClient.get(url, {
      signal: options.abortSignal,
      headers: robotoHeaders({ orgId: options.orgId }),
    });
    return response.json<PaginatedResponse<ImageRepositoryRecord>>();
  }

  /**
   * Delete a repository and every image in it from the org's registry. The
   * API refuses to delete a repository that still holds images unless asked
   * to delete them too, so this always asks.
   */
  public async deleteRepository(
    repositoryName: string,
    options: { orgId: string },
  ): Promise<void> {
    const url = this.#httpClient.constructUrl("v1/images/repository");
    const response = await this.#httpClient.delete(url, {
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify({ repository_name: repositoryName, force: true }),
    });
    return response.throwIfError();
  }

  /** Delete one image, by URI, from the org's registry. */
  public async deleteImage(
    imageUri: string,
    options: { orgId: string },
  ): Promise<void> {
    const url = this.#httpClient.constructUrl("v1/images/image");
    const response = await this.#httpClient.delete(url, {
      headers: robotoHeaders({ orgId: options.orgId }),
      body: JSON.stringify({ image_uri: imageUri }),
    });
    return response.throwIfError();
  }
}
